/**
 * PopupReferee — one owner for the visitor's on-site experience.
 *
 * Each campaign's trigger used to decide alone whether to show, so two
 * popups on one page each passed their own checks and the visitor got both,
 * back to back (RenderEngine queued the second behind the first). And because
 * the server picks a page's campaigns once, at init, a popup armed on one page
 * could fire after an in-page navigation onto a sign-in or consent screen.
 *
 * The referee is asked at the moment of showing — the only moment the SDK
 * knows which page the visitor is really on — and says no when:
 *
 *   1. the page is protected (sign-in, sign-up, password, consent, checkout,
 *      payment, or the business opted it out) — for every on-site message;
 *   2. a popup already showed on this page view;
 *   3. a popup showed less than the cooldown ago, on any page of the session.
 *
 * A "yes" CLAIMS the slot at once, before the render chunk's await, so two
 * triggers firing together cannot both pass. A render that fails gives the
 * slot back with `release`.
 *
 * The session cap ("two messages per session") stays in InstructionExecutor,
 * which already counts impressions across pages.
 */

import {
  ONSITE_OPT_OUT_META,
  ONSITE_POLICY,
  protectedPathReason,
  type ProtectedPageReason,
} from '@hamla/shared/onsite';

export type SuppressReason = ProtectedPageReason | 'popup_on_this_page' | 'popup_cooldown';

export type RefereeDecision = { allow: true } | { allow: false; reason: SuppressReason };

export interface PopupRefereeLimits {
  popupsPerPageView?: number;
  popupCooldownMs?: number;
}

export interface PopupRefereeEnv {
  now?: () => number;
  /** The current page's path and query; read on every decision. */
  currentPage?: () => string;
  /** The current page's protected reason from its content, or null. */
  pageSignal?: () => ProtectedPageReason | null;
}

/** sessionStorage, for the same reason as InstructionExecutor: it survives navigation and dies with the tab. */
const LAST_POPUP_KEY = 'hamla_last_popup_v1';

function readLastPopupAt(): number | null {
  try {
    const raw = sessionStorage.getItem(LAST_POPUP_KEY);
    const at = raw ? Number(raw) : NaN;
    return Number.isFinite(at) ? at : null;
  } catch {
    return null;
  }
}

function writeLastPopupAt(at: number | null): void {
  try {
    if (at === null) sessionStorage.removeItem(LAST_POPUP_KEY);
    else sessionStorage.setItem(LAST_POPUP_KEY, String(at));
  } catch {
    // Storage unavailable — the in-memory copy still guards this page.
  }
}

function isVisible(el: Element): boolean {
  const withCheck = el as Element & { checkVisibility?: () => boolean };
  if (typeof withCheck.checkVisibility === 'function') return withCheck.checkVisibility();
  return el.getClientRects().length > 0;
}

/**
 * What the page itself says about being protected. A visible password field
 * or card field protects any URL — this is what catches a sign-in page at a
 * path no pattern knows. Visible only: plenty of stores keep a hidden login
 * form in the header of every page.
 */
export function readPageSignal(doc: Document | undefined = globalThis.document): ProtectedPageReason | null {
  if (!doc) return null;
  const meta = doc.querySelector(`meta[name="${ONSITE_OPT_OUT_META}"]`);
  if (meta?.getAttribute('content')?.trim().toLowerCase() === 'off') return 'opted_out';
  for (const el of Array.from(doc.querySelectorAll('input[type="password"]'))) {
    if (isVisible(el)) return 'password';
  }
  for (const el of Array.from(doc.querySelectorAll('input[autocomplete^="cc-"]'))) {
    if (isVisible(el)) return 'payment';
  }
  return null;
}

function currentPageFromLocation(): string {
  if (typeof location === 'undefined') return '';
  return `${location.pathname}${location.search}`;
}

export class PopupReferee {
  private readonly popupsPerPageView: number;
  private readonly cooldownMs: number;
  private readonly now: () => number;
  private readonly currentPage: () => string;
  private readonly pageSignal: () => ProtectedPageReason | null;

  private page = '';
  private popupsOnPage = 0;
  private lastPopupAt: number | null;
  private previousLastPopupAt: number | null = null;

  constructor(limits: PopupRefereeLimits = {}, env: PopupRefereeEnv = {}) {
    this.popupsPerPageView = limits.popupsPerPageView ?? ONSITE_POLICY.popupsPerPageView;
    this.cooldownMs = limits.popupCooldownMs ?? ONSITE_POLICY.popupCooldownMs;
    this.now = env.now ?? Date.now;
    this.currentPage = env.currentPage ?? currentPageFromLocation;
    this.pageSignal = env.pageSignal ?? (() => readPageSignal());
    this.lastPopupAt = readLastPopupAt();
  }

  /**
   * Ask to show a message now. A popup that is allowed takes its slot
   * immediately.
   *
   * `repeat` is the same message showing again because its author asked for
   * every trigger fire. It skips the cooldown, being the message the visitor
   * already saw, but it is still this page's one popup. Letting it through
   * without taking the slot left a second popup "allowed" on hamla.io while
   * the every-page bar was open: the render engine queued it behind the bar
   * for good and the visit's second slot was spent on nothing.
   *
   * `allowProtected` is the protected pages this campaign's author chose to
   * show on anyway (the builder's "Don't show on" list). The site's own
   * opt-out tag is never in it.
   */
  claim(input: {
    isPopup: boolean;
    repeat?: boolean;
    allowProtected?: readonly string[];
  }): RefereeDecision {
    const page = this.currentPage();
    // The site's opt-out tag always wins. Otherwise the URL names the page
    // first and its content only speaks when the URL says nothing: a checkout
    // carries a card field, and reading that as "payment" blocked a campaign
    // whose author had chosen to show on checkout — found by the end-to-end run.
    const signal = this.pageSignal();
    const protectedReason =
      signal === 'opted_out' ? signal : (protectedPathReason(page) ?? signal);
    if (
      protectedReason &&
      (protectedReason === 'opted_out' || !input.allowProtected?.includes(protectedReason))
    ) {
      return { allow: false, reason: protectedReason };
    }

    if (!input.isPopup) return { allow: true };

    if (page !== this.page) {
      this.page = page;
      this.popupsOnPage = 0;
    }
    if (this.popupsOnPage >= this.popupsPerPageView) {
      return { allow: false, reason: 'popup_on_this_page' };
    }
    if (input.repeat) {
      this.popupsOnPage++;
      return { allow: true };
    }
    const now = this.now();
    if (this.lastPopupAt !== null && now - this.lastPopupAt < this.cooldownMs) {
      return { allow: false, reason: 'popup_cooldown' };
    }

    this.popupsOnPage++;
    this.previousLastPopupAt = this.lastPopupAt;
    this.lastPopupAt = now;
    writeLastPopupAt(now);
    return { allow: true };
  }

  /** Give back the slot a popup claimed but never showed. */
  release(): void {
    if (this.popupsOnPage > 0) this.popupsOnPage--;
    this.lastPopupAt = this.previousLastPopupAt;
    writeLastPopupAt(this.lastPopupAt);
  }
}
